import { Box, Stack, styled, Typography } from "@mui/material"

import { Skeleton } from "@mui/material"
import { useQuery } from "@tanstack/react-query"

import { PoolDetails, TokenDetails } from "./SwapMultiHopRoute"
import { DcaOrderFormValues } from "./types/DcaOrderForm"
import { LimitOrderFormValues } from "./types/LimitOrderForm"
import { StopLosOrderFormValues } from "./types/StopLossOrderForm"
import { OrderType } from "./types/swap-types"
import { getDcaParams } from "../../ao/agents-api"
import { useAo } from "../../ao/ao"
import { TokenInfo } from "../../ao/token"
import { PRICE_DECIMALS } from "../../settings"
import { NumberFont } from "../../theme"
import {
  formatFullDateFromMinutes,
  formatDuration,
  formatNumber,
  formatNumberAuto,
  formatTicker,
} from "../../utils/format"

type SwapOrderDetailsProps = {
  orderType: OrderType
  orderExtra: LimitOrderFormValues | DcaOrderFormValues | StopLosOrderFormValues | null
  feePercentageBps?: bigint
  receiveAtLeast?: string | null
  slippage?: number
  fromToken?: TokenInfo
  toToken?: TokenInfo
  price?: number | null
  priceImpact?: string
  fee?: string
  multiHop?: {
    pool_addresses: string[]
    distance: number
    path: string[]
    amount_out: string
  }
}

export function SwapOrderDetails(props: SwapOrderDetailsProps) {
  const {
    feePercentageBps,
    receiveAtLeast,
    slippage,
    fromToken,
    toToken,
    price,
    priceImpact,
    fee,
    orderType,
    orderExtra,
    multiHop,
  } = props

  const ao = useAo()

  const { data: dcaParams, isLoading } = useQuery({
    queryKey: ["dcaParams", orderExtra],
    queryFn: async () => {
      const result = await getDcaParams((orderExtra as DcaOrderFormValues).runningTime, ao)
      console.log("onDcaParams", result)
      return result
    },
    enabled: orderType === "dca",
  })

  return (
    <>
      {price !== undefined && orderType !== "dca" && (
        <DetailsRow>
          <span>Rate</span>
          {price !== null ? (
            <span data-testid="swapRate">
              1 {formatTicker(toToken?.ticker, true)} = {formatNumberAuto(price)}{" "}
              {formatTicker(fromToken?.ticker, true)}
            </span>
          ) : (
            <Skeleton width={60} />
          )}
        </DetailsRow>
      )}
      {multiHop ? (
        <>
          {typeof receiveAtLeast !== "undefined" && (
            <DetailsRow>
              <span>Receive approximately</span>
              {typeof receiveAtLeast === "string" ? (
                <span data-testid="receiveAtLeast">
                  ~{" "}
                  {formatNumber(receiveAtLeast, {
                    minimumFractionDigits: toToken?.denomination,
                    maximumFractionDigits: toToken?.denomination,
                  })}{" "}
                  {formatTicker(toToken?.ticker, true)}
                </span>
              ) : (
                <Skeleton width={60} />
              )}
            </DetailsRow>
          )}
          {typeof feePercentageBps === "bigint" && (
            <>
              <DetailsRow>
                <span>Approximate transaction cost</span>
                <span data-testid="fee">
                  {formatNumberAuto(fee)} {formatTicker(fromToken?.ticker, true)}
                </span>
              </DetailsRow>
              <DetailsRow>
                <span>Network cost</span>
                <span>0 AO</span>
              </DetailsRow>
            </>
          )}
          <Stack
            sx={{ mt: 2 }}
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "flex-start", sm: "space-between" }}
          >
            <Box color="secondary.main">Route</Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              {multiHop.pool_addresses.map((pool, idx) => (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      display: { xs: "inline", sm: "none" },
                      mr: "1ch",
                      fontFamily: NumberFont,
                    }}
                  >
                    {idx + 1}.
                  </Box>
                  <PoolDetails key={pool} pool={pool} />
                  {multiHop &&
                    pool !== multiHop.pool_addresses[multiHop.pool_addresses.length - 1] && (
                      <Box
                        sx={{
                          display: { xs: "none", sm: "inline" },
                        }}
                      >
                        →
                      </Box>
                    )}
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "flex-start", sm: "space-between" }}
            mt={{ xs: 2, sm: 1 }}
          >
            <Box color="secondary.main">Path used</Box>
            <Stack gap={1} direction="row" alignItems={{ xs: "flex-start", sm: "center" }}>
              {multiHop.path.map((path) => (
                <Stack direction="row" gap={1} alignItems="center">
                  <TokenDetails key={path} token={path} />
                  {multiHop && path !== multiHop.path[multiHop.path.length - 1] && <Box>→</Box>}
                </Stack>
              ))}
            </Stack>
          </Stack>
        </>
      ) : (
        <>
          {orderType === "swap" && (
            <>
              {typeof priceImpact === "string" && (
                <DetailsRow>
                  <span>Price impact</span>
                  <Typography
                    variant="inherit"
                    component="span"
                    color={parseFloat(priceImpact) < -2 ? "error" : "inherit"}
                    data-testid="priceImpact"
                  >
                    {priceImpact}%
                  </Typography>
                </DetailsRow>
              )}
              {typeof slippage === "number" && (
                <DetailsRow>
                  <span>Max slippage</span>
                  <span data-testid="slippage">{slippage}%</span>
                </DetailsRow>
              )}
              {typeof receiveAtLeast !== "undefined" && (
                <DetailsRow>
                  <span>Receive at least</span>
                  {typeof receiveAtLeast === "string" ? (
                    <span data-testid="receiveAtLeast">
                      {formatNumber(receiveAtLeast, {
                        minimumFractionDigits: toToken?.denomination,
                        maximumFractionDigits: toToken?.denomination,
                      })}{" "}
                      {formatTicker(toToken?.ticker, true)}
                    </span>
                  ) : (
                    <Skeleton width={60} />
                  )}
                </DetailsRow>
              )}
            </>
          )}
          {orderType === "dca" && (
            <>
              <DetailsRow>
                <span>Buy Frequency</span>
                <span data-testid="dcaFrequency">
                  {isLoading ? (
                    <Skeleton width={60} />
                  ) : (
                    formatDuration(dcaParams?.tickInterval, "seconds")
                  )}
                </span>
              </DetailsRow>
              <DetailsRow>
                <span>Total Buys</span>
                <span data-testid="dcaTotalBuys">
                  {isLoading ? <Skeleton width={60} /> : dcaParams?.totalTicks}
                </span>
              </DetailsRow>
              <DetailsRow>
                <span>Approximate end date</span>
                <span data-testid="dcaEndDate">
                  {formatFullDateFromMinutes((orderExtra as DcaOrderFormValues)?.runningTime)}
                </span>
              </DetailsRow>
            </>
          )}
          {price !== null && orderType === "limit" && (
            <>
              <DetailsRow>
                <span>Limit Price</span>
                <span data-testid="limitPrice">
                  {formatNumberAuto(
                    (orderExtra as LimitOrderFormValues)?.limitPrice,
                    PRICE_DECIMALS,
                  )}{" "}
                  {formatTicker(fromToken?.ticker, true)}
                </span>
              </DetailsRow>
            </>
          )}
          {(orderType === "limit" || orderType === "stop-loss") && (
            <>
              <DetailsRow>
                <span>Expires at</span>
                <span data-testid="expiry">
                  {formatFullDateFromMinutes((orderExtra as LimitOrderFormValues)?.expiry)}
                </span>
              </DetailsRow>
            </>
          )}
          {price !== null &&
            orderType === "stop-loss" &&
            (orderExtra as StopLosOrderFormValues)?.variant !== "trailing" && (
              <>
                <DetailsRow>
                  <span>Stop Loss Trigger Price</span>
                  <span data-testid="stopLossPrice">
                    {(orderExtra as StopLosOrderFormValues)?.stopLossPrice}{" "}
                    {formatTicker(fromToken?.ticker, true)}
                  </span>
                </DetailsRow>
              </>
            )}
          {price !== null &&
            orderType === "stop-loss" &&
            (orderExtra as StopLosOrderFormValues)?.variant === "limit" && (
              <>
                <DetailsRow>
                  <span>Stop Loss Sell Price</span>
                  <span data-testid="stopLossSellPrice">
                    {(orderExtra as StopLosOrderFormValues)?.sellPrice}{" "}
                    {formatTicker(fromToken?.ticker, true)}
                  </span>
                </DetailsRow>
              </>
            )}
          {price !== null &&
            orderType === "stop-loss" &&
            (orderExtra as StopLosOrderFormValues)?.variant === "trailing" && (
              <>
                <DetailsRow>
                  <span>Trailing Stop Loss</span>
                  <span data-testid="trailingStopLoss">
                    {(orderExtra as StopLosOrderFormValues)?.callback} %
                  </span>
                </DetailsRow>
              </>
            )}
          {typeof feePercentageBps === "bigint" && (
            <>
              <DetailsRow>
                <span>Fee ({Number(feePercentageBps) / 100}%)</span>
                <span data-testid="fee">
                  {formatNumberAuto(fee)} {formatTicker(fromToken?.ticker, true)}
                </span>
              </DetailsRow>
              <DetailsRow>
                <span>Network cost</span>
                <span>0 AO</span>
              </DetailsRow>
            </>
          )}
        </>
      )}
    </>
  )
}

const DetailsRow = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  gap: var(--spacing);

  & > span:first-of-type {
    white-space: nowrap;
    color: var(--mui-palette-secondary-main);
  }

  & > span:last-of-type {
    font-family: ${NumberFont};
    text-align: right;
  }

  & > div:last-of-type {
    font-family: ${NumberFont};
    text-align: right;
  }
`
